import React from 'react';

/**
 * 휴무 관리 두 탭이 함께 쓰는 조각들 — 표 칸·요약 카드·고르기 상자와 셈 몇 개.
 *
 * <p>계획서 탭과 연차 현황 탭은 <b>같은 표를 다른 물음으로</b> 읽는다(계획대로 쉬는가 / 얼마나 썼는가).
 * 두 화면의 칸 높이·머리행 색이 조금씩 달라지면 탭을 오갈 때마다 다른 시스템처럼 보이므로, 눈에
 * 보이는 규격은 여기 한 곳에 둔다.
 */

export const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];
export const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

export function StatCard({ color, label, value, note }: { color: string; label: string; value: string; note: string }) {
  return (
    <div className="card p-4" style={{ borderLeft: `3px solid ${color}` }}>
      <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
      <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{note}</p>
    </div>
  );
}

export function Picker({
  value,
  onChange,
  all,
  options,
  label,
  suffix = '',
}: {
  value: string;
  onChange: (value: string) => void;
  all: string;
  options: string[];
  label: string;
  suffix?: string;
}) {
  return (
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className="input-field"
      // 칸마다 폭이 다르면 줄이 들쭉날쭉해진다. 넷을 같은 폭으로 묶어 둔다.
      style={{ width: '116px', fontSize: '13px' }}
      aria-label={label}
    >
      <option value="">{all}</option>
      {options.map((option) => (
        <option key={option} value={option}>{option}{suffix}</option>
      ))}
    </select>
  );
}

export const cellStyle: React.CSSProperties = {
  borderBottom: '1px solid var(--border)',
  borderRight: '1px solid var(--border)',
  padding: '4px 6px',
};

export const headStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 2,
  background: '#f8fafc',
  borderBottom: '1px solid var(--border)',
  borderRight: '1px solid var(--border)',
  padding: '6px',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

export function HeadCell({
  children,
  align = 'left',
  style,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  style?: React.CSSProperties;
}) {
  return <th style={{ ...headStyle, textAlign: align, ...style }}>{children}</th>;
}

export function BodyCell({
  children,
  align = 'left',
  background = 'white',
  colSpan,
  style,
}: {
  children?: React.ReactNode;
  align?: 'left' | 'center' | 'right';
  background?: string;
  /** 여러 칸을 하나로 — 「계획 미제출」 처럼 숫자 칸 전부를 한마디가 대신할 때만 쓴다. */
  colSpan?: number;
  style?: React.CSSProperties;
}) {
  return <td colSpan={colSpan} style={{ ...cellStyle, textAlign: align, background, ...style }}>{children}</td>;
}

/** 진척 막대 하나. 이행률은 숫자보다 길이로 먼저 읽힌다 — 100% 를 넘길 수 없게 잘라 둔다. */
export function ProgressBar({ ratio, color = '#2563eb', width = '100%' }: { ratio: number; color?: string; width?: string }) {
  return (
    <span
      style={{
        display: 'inline-block',
        width,
        height: '8px',
        borderRadius: '4px',
        background: 'var(--bg-hover)',
        overflow: 'hidden',
        verticalAlign: 'middle',
      }}
    >
      <span
        style={{
          display: 'block',
          height: '100%',
          width: `${Math.min(100, Math.max(0, ratio * 100))}%`,
          background: color,
        }}
      />
    </span>
  );
}

/** 부여가 없으면 「-」 다. 0 으로 적으면 「연차 0일」 로 읽히는데 그것은 아직 부여가 안 들어온 것이다. */
export function format(value: number | undefined): string {
  return value == null ? '-' : String(value);
}

export function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0);
}

/** 반차가 섞이면 0.5 단위가 나온다. 소수점 한 자리까지만 — 그 아래는 읽는 사람에게 뜻이 없다. */
export function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/** `2026-09-14` → `9/14`. 표 칸에서는 해가 이미 위에 적혀 있다. */
export function shortDate(date: string): string {
  return `${Number(date.slice(5, 7))}/${Number(date.slice(8, 10))}`;
}

/** 오늘(로컬). `toISOString()` 은 UTC 로 밀려 자정 무렵에 하루가 어긋난다. */
export function todayIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

// ── 계획 보기 (휴무 관리 · 마이페이지가 함께 쓴다) ────────────────────────────
//
// 두 화면이 같은 물음에 답한다 — 「계획대로 쉬고 있는가」. 인사 표는 <b>남의 것까지</b>, 마이페이지는
// <b>내 것만</b> 볼 뿐이라, 그림이 다르면 같은 숫자를 두 번 배우게 된다(사용자 지시 2026-08-26).

export const PLAN_COLOR = '#94a3b8';
export const DONE_COLOR = '#2563eb';

export type PlanDay = { date: string; days: number; fulfilled: boolean };

export type PlanProgress = {
  planned: number;
  done: number;
  /** 이미 지난 날인데 쉬지 않은 계획. 다시 시기를 정해야 하는 유일한 근거다. */
  missed: number;
  /** 아직 오지 않은 계획. */
  upcoming: number;
  ratio: number;
  nextDate: string | null;
};

/**
 * 한 사람의 진척. {@code fulfilled} 은 <b>서버가 판정한 값</b>이라 여기서 날짜를 맞춰 보지 않는다 —
 * 지난 날인가({@code missed})만 오늘로 가른다.
 */
export function measurePlans(plans: PlanDay[], today: string): PlanProgress {
  const planned = sum(plans.map((plan) => plan.days));
  const done = sum(plans.filter((plan) => plan.fulfilled).map((plan) => plan.days));
  const missed = sum(plans.filter((plan) => !plan.fulfilled && plan.date < today).map((plan) => plan.days));
  const next = plans.find((plan) => !plan.fulfilled && plan.date >= today) ?? null;

  return {
    planned,
    done,
    missed,
    upcoming: round(planned - done - missed),
    ratio: planned > 0 ? done / planned : 0,
    nextDate: next?.date ?? null,
  };
}

/** 진척 한 줄 — 막대·퍼센트·숫자. 카드 머리에 그대로 얹는다. */
export function PlanProgressStrip({ progress }: { progress: PlanProgress }) {
  return (
    <>
      <span className="flex items-center gap-2" style={{ minWidth: '180px' }}>
        <ProgressBar ratio={progress.ratio} width="120px" />
        <b style={{ fontSize: '13px', color: DONE_COLOR }}>{Math.round(progress.ratio * 100)}%</b>
      </span>
      <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
        계획 {round(progress.planned)}일 · 사용 {round(progress.done)}일 · 남은 계획 {round(progress.upcoming)}일
        {progress.nextDate && ` · 다음 ${shortDate(progress.nextDate)}`}
      </span>
      {progress.missed > 0 && (
        <span className="badge badge-yellow">지난 계획 {round(progress.missed)}일 미이행</span>
      )}
    </>
  );
}

/**
 * 달별 계획과 이행. 한 막대 안에 <b>이행분을 채우고 나머지를 옅게</b> 남긴다 — 계획 막대와 실적
 * 막대를 따로 세우면 "계획대로 갔는가" 를 눈으로 맞춰 봐야 하고, 그것이 이 그림의 유일한 물음이다.
 *
 * <p>막대 머리에 이행률, 올리면 계획·이행·이행률 세 줄(사용자 요청 2026-08-26). 계획이 없는 달은
 * 「계획 없음」 이라고 적는다 — 0% 로 적으면 계획을 내고 안 지킨 달처럼 읽힌다.
 */
export function PlanMonthChart({
  year,
  plans,
  title,
  note,
}: {
  year: number;
  plans: PlanDay[];
  title?: string;
  note?: React.ReactNode;
}) {
  const bars = React.useMemo(() => {
    const planned = new Array<number>(12).fill(0);
    const done = new Array<number>(12).fill(0);
    for (const plan of plans) {
      const index = Number(plan.date.slice(5, 7)) - 1;
      if (index < 0 || index > 11) continue;
      planned[index] = (planned[index] ?? 0) + plan.days;
      if (plan.fulfilled) done[index] = (done[index] ?? 0) + plan.days;
    }
    return MONTH_LABELS.map((label, index) => {
      const monthPlanned = planned[index]!;
      const monthDone = done[index]!;
      return { label, planned: monthPlanned, done: monthDone, ratio: monthPlanned > 0 ? monthDone / monthPlanned : 0 };
    });
  }, [plans]);

  const peak = Math.max(...bars.map((bar) => bar.planned), 1);
  const [hovered, setHovered] = React.useState<number | null>(null);

  return (
    // `h-full` + 세로 흐름 — 옆 카드와 키를 맞춘 자리에서 막대 칸이 남는 높이를 먹는다. 혼자 놓이면
    // 높이가 자동이라 아래 `minHeight` 가 그대로 예전 키(146px)다.
    <div className="card h-full flex flex-col" style={{ padding: '12px 14px' }}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>
          {title ?? `${year}년 달별 계획 · 이행`}
        </h2>
        <span className="flex items-center gap-3" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          <span className="flex items-center gap-1">
            <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: DONE_COLOR }} /> 계획대로 사용
          </span>
          <span className="flex items-center gap-1">
            <span style={{ width: '10px', height: '10px', borderRadius: '2px', background: PLAN_COLOR }} /> 아직 남은 계획
          </span>
          {note ?? <span>막대 위에 올리면 그 달의 계획·이행·이행률</span>}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '6px', flex: 1, minHeight: '146px', marginTop: '10px' }}>
        {bars.map((bar, index) => {
          const on = hovered === index;
          const percent = Math.round(bar.ratio * 100);
          return (
            // 막대 하나가 버튼이다 — 마우스뿐 아니라 <b>키보드 초점</b>으로도 같은 말풍선이 열린다.
            <button
              key={bar.label}
              type="button"
              onMouseEnter={() => setHovered(index)}
              onMouseLeave={() => setHovered((current) => (current === index ? null : current))}
              onFocus={() => setHovered(index)}
              onBlur={() => setHovered((current) => (current === index ? null : current))}
              aria-label={`${bar.label} 계획 ${round(bar.planned)}일 · 이행 ${round(bar.done)}일 · 이행률 ${percent}%`}
              style={{
                position: 'relative',
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                height: '100%',
                border: 'none',
                background: on ? 'var(--bg-hover)' : 'transparent',
                borderRadius: '4px',
                padding: 0,
                cursor: 'default',
              }}
            >
              {/* 말풍선은 막대 <b>위</b>에 띄운다 — 옆에 붙이면 12월 칸에서 카드 밖으로 밀린다. */}
              {on && (
                <span
                  style={{
                    position: 'absolute',
                    bottom: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginBottom: '4px',
                    zIndex: 5,
                    whiteSpace: 'nowrap',
                    borderRadius: '6px',
                    background: 'rgba(15, 23, 42, 0.92)',
                    color: '#ffffff',
                    padding: '5px 8px',
                    fontSize: '11px',
                    lineHeight: 1.5,
                    textAlign: 'left',
                    pointerEvents: 'none',
                  }}
                >
                  <b>{year}년 {bar.label}</b>
                  <span style={{ display: 'block' }}>계획 {round(bar.planned)}일</span>
                  <span style={{ display: 'block' }}>이행 {round(bar.done)}일</span>
                  <span style={{ display: 'block', fontWeight: 700 }}>
                    {bar.planned > 0 ? `이행률 ${percent}%` : '계획 없음'}
                  </span>
                </span>
              )}

              {/* 막대 머리에 <b>이행률</b>, 그 아래 계획 일수. 묻는 것이 진척이라 %가 위에 선다. */}
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textAlign: 'center',
                  color: bar.planned ? (percent >= 100 ? '#15803d' : DONE_COLOR) : 'transparent',
                }}
              >
                {bar.planned ? `${percent}%` : '·'}
              </span>
              <span
                style={{
                  fontSize: '9px',
                  textAlign: 'center',
                  color: bar.planned ? 'var(--text-muted)' : 'transparent',
                  marginBottom: '2px',
                }}
              >
                {bar.planned ? `${round(bar.planned)}일` : '·'}
              </span>
              <span
                style={{
                  display: 'block',
                  height: `${((bar.planned - bar.done) / peak) * 80}px`,
                  background: PLAN_COLOR,
                  borderRadius: '3px 3px 0 0',
                }}
              />
              <span
                style={{
                  display: 'block',
                  height: `${(bar.done / peak) * 80}px`,
                  background: DONE_COLOR,
                  borderRadius: bar.planned > bar.done ? 0 : '3px 3px 0 0',
                }}
              />
              <span
                style={{
                  fontSize: '10px',
                  textAlign: 'center',
                  marginTop: '3px',
                  fontWeight: on ? 700 : 400,
                  color: on ? 'var(--text-primary)' : 'var(--text-muted)',
                }}
              >
                {bar.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** 계획한 날들. <b>이행·예정·미이행</b> 셋으로만 갈라 적는다 — 계획서를 읽는 이유가 그것이다. */
export function PlanDateList({ plans, today }: { plans: PlanDay[]; today: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      {plans.map((plan) => {
        const past = plan.date < today;
        const state = plan.fulfilled ? '사용함' : past ? '미이행' : '예정';
        const color = plan.fulfilled ? DONE_COLOR : past ? 'var(--danger)' : 'var(--text-muted)';
        return (
          <div
            key={plan.date}
            className="flex items-center justify-between"
            style={{ fontSize: '13px', padding: '6px 10px', borderRadius: '6px', background: 'var(--bg-hover)' }}
          >
            <span>{plan.date}</span>
            <span style={{ color, fontWeight: 600 }}>{state} · {round(plan.days)}일</span>
          </div>
        );
      })}
    </div>
  );
}
